import { KeyboardArrowDown } from '@mui/icons-material'
import { Popover } from '@mui/material'
import { useMemo, useRef, useState } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useSwitchOrganisation } from '@omnea-app/fe-auth/useSwitchOrganisation'
import { FuzzySearchResult } from '@omnea-app/frontend/utils/string/fuzzySearchList'
import { AvailableOrganisation } from '@omnea-app/login/loginFrontendClient'
import { Wrapper } from './OrganisationIndicator'
import { OrganisationLogo } from './OrganisationLogo'
import { OrganisationMenuItem } from './OrganisationMenuItem/OrganisationMenuItem'
import { OrganisationSearchBox } from './OrganisationSearchBox'

export interface MultipleOrganisationPickerProps {
  availableOrganisations: AvailableOrganisation[]
}

export const MultipleOrganisationPicker: React.FC<MultipleOrganisationPickerProps> = ({ availableOrganisations }) => {
  const [pickerIsOpen, setPickerIsOpen] = useState(false)
  const popoverAnchorEl = useRef<HTMLDivElement>(null)
  const { organisationId: currentOrganisationId } = useAuthenticatedUser().org
  const currentOrganisation = useMemo(
    () => availableOrganisations.find((org) => org.id === currentOrganisationId),
    [availableOrganisations, currentOrganisationId]
  )

  const switchOrganisation = useSwitchOrganisation()

  const pickOrganisation = async (organisationIdToSwitch: string) => {
    setPickerIsOpen(false)

    if (organisationIdToSwitch === currentOrganisationId) return

    await switchOrganisation(organisationIdToSwitch)
  }

  const [searchResults, setSearchResults] = useState<FuzzySearchResult<AvailableOrganisation>[] | null>(null)

  const organisationsOrResults: AvailableOrganisation[] | FuzzySearchResult<AvailableOrganisation>[] =
    searchResults ?? availableOrganisations

  // We don't need to show the search box for users with only two orgs
  const searchIsEnabled = availableOrganisations.length > 2

  return (
    <Wrapper
      as="button"
      onClick={() => setPickerIsOpen((state) => !state)}
      ref={popoverAnchorEl}
      sx={{
        gap: 1,
        gridTemplateColumns: 'auto 1fr',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        '&:hover': {
          backgroundColor: 'bg.surface.subtle',
        },
      }}
    >
      {currentOrganisation && <OrganisationLogo organisation={currentOrganisation} />}

      <KeyboardArrowDown
        sx={{
          color: 'fg.subtle',
          fontSize: 20,
          transition: 'rotate 0.2s',
          rotate: pickerIsOpen ? '180deg' : '0deg',
        }}
      />

      <Popover
        open={pickerIsOpen}
        anchorEl={popoverAnchorEl.current}
        onClose={(event: Event) => {
          event.stopPropagation()
          setPickerIsOpen(false)
        }}
        disableAutoFocus={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 350,
              maxWidth: 500,
              boxShadow: 3,
              mt: 4,
            },
          },
        }}
      >
        {searchIsEnabled && (
          <OrganisationSearchBox
            availableOrganisations={availableOrganisations}
            onResultsChange={setSearchResults}
            onSubmit={() => searchResults?.[0] && pickOrganisation(searchResults[0].item.id)}
            onEscape={() => setPickerIsOpen(false)}
          />
        )}
        {organisationsOrResults.map((organisationOrResult, index) => {
          const organisation = 'highlightedRanges' in organisationOrResult ? organisationOrResult.item : organisationOrResult
          const highlightedRanges = 'highlightedRanges' in organisationOrResult ? organisationOrResult.highlightedRanges : null

          return (
            <OrganisationMenuItem
              organisation={organisation}
              highlightedRanges={highlightedRanges}
              handlePickOrganisation={pickOrganisation}
              isFirst={index === 0}
              key={organisation.id}
              isQaOrg={organisation.isQa}
            />
          )
        })}
      </Popover>
    </Wrapper>
  )
}
